/**
 * # 项目时区前置初始化
 *
 * 设计模式与 `@/i18n` 一致：
 * - `initProjectTimezone()` 在 `main.ts` 中 await，确保业务 JS 执行前时区已就绪。
 * - 导出的 `projectTimezone` 可在模块初始化时序中直接使用，无需 Pinia。
 *
 * 与 i18n 不同的是，时区依赖当前 projectCode（从 URL 提取），
 * 因此切换项目时必须 full reload 以保证时区正确。
 *
 * 时区始终以 `UTC`、`UTC+N` 或 `UTC+N:MM` 格式表达（如 `UTC`、`UTC+8`、`UTC-5`、`UTC+5:30`），
 * 无论接口返回还是降级兜底，都统一为此格式。
 *
 * @module project-timezone
 */

import axios from 'axios';
import { LOCALE } from '@/i18n';
import { getRequestBaseURL } from '@/request/standalone-base-url';
import { canUseDevProxyAuthentication, getAuthToken } from '@/request/standalone-auth-token';
import { isTransportError, isRequestCancellation } from '@/request/transport-error';
import { reportException } from '@/mod/monitoring/exception';
import { reportDegradation } from '@/mod/monitoring/diagnostics';
import { getProjectCodeFromPathname } from '@/utils/navigation-helper';
import { parseProjectTimezoneOffsetMinutes } from './project-timezone-offset';

/** 接口不可用时的固定降级时区 */
const FALLBACK_TIMEZONE = 'UTC+8';

// ---------------------------------------------------------------------------
// 模块级状态 —— 一旦 initProjectTimezone() resolve，全局可用
// ---------------------------------------------------------------------------

/**
 * 当前项目的时区偏移（`UTC+N` / `UTC+N:MM` 格式），业务代码通过 getter 读取。
 *
 * 仅 {@link initProjectTimezone} 允许写入，外部只读。
 *
 * > **此处使用 mutable export 是刻意选择**，与 `@/i18n` 中 `LOCALE` 保持一致的范式：
 * > ESM live binding 保证外部模块始终读到最新值，同时无需引入 Pinia / reactive 包装。
 */
let _projectTimezone: string = FALLBACK_TIMEZONE;

export { _projectTimezone as projectTimezone };

// ---------------------------------------------------------------------------
// 内部工具
// ---------------------------------------------------------------------------

// getProjectCodeFromPathname 从 @/utils/navigation-helper 统一引入

/**
 * 独立的 axios 实例，仅用于时区初始化请求。
 *
 * 不能复用 dataSourceServer（@/request/data-source-api），因为存在循环依赖：
 *   project-timezone → data-source-api → request/index → interceptors → store/user → project-timezone
 *
 * 因此这里直接 axios.create()，不挂拦截器，手动对齐必要 header。
 * baseURL 从 @/request/standalone-base-url 获取，与其他 axios 实例保持一致的推导逻辑，
 * 该模块仅依赖 runtime context，不会引入循环依赖。
 */
const timezoneAxios = axios.create({
  baseURL: getRequestBaseURL(),
  timeout: 5000,
  // 防御性覆盖：全局 axios.defaults.validateStatus 被设为 () => true，
  // 此处显式覆盖，确保非 2xx 状态码走 rejected 分支，由外层 catch 统一降级处理。
  validateStatus: status => status >= 200 && status < 300,
});

/** tRPC 接口路径 —— 变更须与后端同步 */
const TZ_API_PATH = '/trpc.tab.tab_olap_data_source.TabOlapDataSource/GetBusinessTimeZone';

const fetchBusinessTimeZone = async (businessCode: number, token: string | null): Promise<unknown> => {
  const { data } = await timezoneAxios.post(
    TZ_API_PATH,
    { business_code: businessCode },
    {
      headers: {
        'X-Tab-RPC-ServiceName': 'grpc.tab.tab_olap_data_source.data_source_service_http',
        'X-Tab-Language': LOCALE,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );

  // P0-2: 使用 ?? 替代 ||，避免空字符串 "" 被误判为 falsy 而降级
  // 接口响应结构为 { status, msg, data: "UTC+N" }，时区值在通用包装的 data 字段
  return data?.data ?? undefined;
};

// ---------------------------------------------------------------------------
// 初始化（main.ts 调用一次）
// ---------------------------------------------------------------------------

/** 重入保护：防止 HMR 或意外多次调用 */
let _initialized = false;

/**
 * 调用 `GetBusinessTimeZone` tRPC 接口获取项目时区。
 *
 * - 有 projectCode 且有浏览器或本地代理认证时发起请求，将结果写入 {@link projectTimezone}。
 * - 无 projectCode（首页等）则保持默认值 `UTC+8`，进入项目页 full reload 后再拉接口。
 * - 接口异常或返回无时区：回落到固定的 `UTC+8`，不阻塞应用启动，
 *   直接提交诊断，不制造未捕获异常。
 * - 接口返回格式不合法（如 IANA 名 `America/New_York`）：视为异常，降级并告警。
 */
export const initProjectTimezone = async (): Promise<void> => {
  // P1-3: 重入保护，HMR/开发模式下避免多次初始化
  if (_initialized) return;
  _initialized = true;

  const projectCode = getProjectCodeFromPathname();
  if (!projectCode) {
    return;
  }

  const projectCodeNum = Number(projectCode);
  if (!Number.isFinite(projectCodeNum)) {
    console.warn(`[project-timezone] Invalid projectCode "${projectCode}", falling back to ${FALLBACK_TIMEZONE}`);
    return;
  }

  try {
    const token = getAuthToken();
    if (!token && !canUseDevProxyAuthentication(getRequestBaseURL())) return;
    const raw = await fetchBusinessTimeZone(projectCodeNum, token);
    if (typeof raw === 'string' && parseProjectTimezoneOffsetMinutes(raw) !== null) {
      _projectTimezone = raw.trim();
      return;
    }
  } catch (error) {
    if (!isTransportError(error) && !isRequestCancellation(error)) {
      reportException(error, 'project-timezone');
    }
  }
  // 请求故障或响应非法均沿用默认值；请求事实由 API 监控承接。
  _projectTimezone = FALLBACK_TIMEZONE;
  reportDegradation({ code: 'TIMEZONE_DEFAULTED', source: 'project-timezone' });
};

// ---------------------------------------------------------------------------
// HMR 支持（仅开发环境）
// ---------------------------------------------------------------------------

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    _initialized = false;
  });
}
